import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    AREA_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { agentFilterId, siteFilterId, testFilterId, timeRangeFilterId, useAgent } from '../../filters';
import PerformanceMinMax from '../../charts/performance-min-max/PerformanceMinMax';
import usePanelDataService from '../../../services/PanelDataService';

const title = t`Service Test Performance`;

const testTableDataSource = {
    url: '/query/table',
    params: {
        type: 'test',
        metrics: 'availPercent,*primary*',
    },
};

const testAgentTableDataSource = {
    url: '/query/table',
    params: {
        type: 'test,agent',
        metrics: 'availPercent,*primary*',
    },
};

const tableSourceFn = (agent) => (agent ? testAgentTableDataSource : testTableDataSource);

const testChartDataSource = {
    url: '/query/chart',
    params: {
        type: 'test',
        metrics: 'status,*,*thresholds*,pathHops,pathLatency',
    },
};

const testAgentChartDataSource = {
    url: '/query/chart',
    params: {
        type: 'test,agent',
        metrics: 'status,*,*thresholds*,pathHops,pathLatency',
    },
};

const chartSourceFn = (agent) => (agent ? testAgentChartDataSource : testChartDataSource);

function PerfChart({ showPathPerformance, tableData, chartData }) {
    const test = tableData?.entities?.test;
    if (!test) return null;

    const pathPerformance = showPathPerformance && test?.traceRoute?.enabled;

    return (
        <PerformanceMinMax
            test={test}
            showPathPerformance={pathPerformance}
            chartData={chartData.data[0]}
            tableData={tableData.data[0]}
        />
    );
}

function ServiceTestPerformancePanel() {
    const agent = useAgent();

    const tableSource = useMemo(() => tableSourceFn(agent), [agent]);
    const chartSource = useMemo(() => chartSourceFn(agent), [agent]);

    const tableData = usePanelDataService(tableSource);
    const chartData = usePanelDataService(chartSource);

    if (!tableData || !chartData) return null;
    return <PerfChart showPathPerformance tableData={tableData} chartData={chartData} />;
}

ServiceTestPerformancePanel.propTypes = panelPropTypes;

ServiceTestPerformancePanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 250,
    minContentHeight: 150,
};

export default Panel({
    typeAlias: 'TestPerformance',
    catalogEntry: {
        category: PANEL_CATEGORIES.TEST,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Service test performance trend`,
        previewImage: AREA_CHART_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId, testFilterId],
        optional: [agentFilterId, siteFilterId],
    },
})(ServiceTestPerformancePanel);
